import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Calendar, UtensilsCrossed, Plus, Trash2, Pencil, ChevronLeft, ChevronRight, BookOpen, GraduationCap, Flag, ClipboardList, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const DAYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes'];
const DAY_LABELS = { lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves', viernes: 'Viernes' };

const CATEGORIES = [
  { value: 'reunion', label: 'Reunión', color: 'bg-blue-100 text-blue-700', icon: BookOpen },
  { value: 'feriado', label: 'Feriado', color: 'bg-red-100 text-red-700', icon: Flag },
  { value: 'actividad', label: 'Actividad', color: 'bg-green-100 text-green-700', icon: Star },
  { value: 'examen', label: 'Examen', color: 'bg-purple-100 text-purple-700', icon: ClipboardList },
  { value: 'evento', label: 'Evento', color: 'bg-orange-100 text-orange-700', icon: Calendar },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[4];

// Local date helpers (avoid UTC off-by-one in Puerto Rico / UTC-4).
const todayLocalStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Formats a 'YYYY-MM-DD' string for display WITHOUT timezone shift.
const formatLocalDate = (dateStr, options) => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', options);
};

const CalendarsTab = () => {
  const { token } = useAuth();
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const [activeCalendar, setActiveCalendar] = useState('events');

  // Events state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', description: '', event_date: '', end_date: '', category: 'evento', all_day: true, target_grades: null });
  const [eventFilterCategory, setEventFilterCategory] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });

  // Lunch state
  const [lunchMenus, setLunchMenus] = useState([]);
  const [loadingLunch, setLoadingLunch] = useState(true);
  const [showLunchDialog, setShowLunchDialog] = useState(false);
  const [editingLunchId, setEditingLunchId] = useState(null);
  const [lunchForm, setLunchForm] = useState({ week_start: '', week_label: '', items: DAYS.map(d => ({ day: d, menu: '', notes: '' })) });

  useEffect(() => { loadEvents(); loadLunchMenus(); }, []);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await api.get('/api/calendars/events');
      setEvents(res.data || []);
    } catch (e) { toast.error('Error cargando eventos'); }
    finally { setLoadingEvents(false); }
  };

  const loadLunchMenus = async () => {
    setLoadingLunch(true);
    try {
      const res = await api.get('/api/calendars/lunch');
      setLunchMenus(res.data || []);
    } catch (e) { toast.error('Error cargando menús'); }
    finally { setLoadingLunch(false); }
  };

  // --- Events ---
  const openCreateEvent = () => {
    setEditingEventId(null);
    setEventForm({ title: '', description: '', event_date: '', end_date: '', category: 'evento', all_day: true, target_grades: null });
    setShowEventDialog(true);
  };

  const openEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEventForm({ title: ev.title, description: ev.description || '', event_date: ev.event_date, end_date: ev.end_date || '', category: ev.category, all_day: ev.all_day !== false, target_grades: ev.target_grades });
    setShowEventDialog(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.event_date) { toast.error('Título y fecha son requeridos'); return; }
    try {
      if (editingEventId) {
        await api.put(`/api/calendars/events/${editingEventId}`, eventForm);
        toast.success('Evento actualizado');
      } else {
        await api.post('/api/calendars/events', eventForm);
        toast.success('Evento creado');
      }
      setShowEventDialog(false);
      loadEvents();
    } catch (e) { toast.error('Error al guardar evento'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await api.delete(`/api/calendars/events/${id}`); toast.success('Evento eliminado'); loadEvents(); }
    catch (e) { toast.error('Error al eliminar'); }
  };

  // --- Lunch ---
  const getMonday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
  };

  const openCreateLunch = () => {
    setEditingLunchId(null);
    setLunchForm({ week_start: getMonday(), week_label: '', items: DAYS.map(d => ({ day: d, menu: '', notes: '' })) });
    setShowLunchDialog(true);
  };

  const openEditLunch = (menu) => {
    setEditingLunchId(menu.id);
    const items = DAYS.map(d => {
      const existing = menu.items?.find(i => i.day === d);
      return existing || { day: d, menu: '', notes: '' };
    });
    setLunchForm({ week_start: menu.week_start, week_label: menu.week_label || '', items });
    setShowLunchDialog(true);
  };

  const updateLunchItem = (dayIdx, field, value) => {
    const items = [...lunchForm.items];
    items[dayIdx] = { ...items[dayIdx], [field]: value };
    setLunchForm({ ...lunchForm, items });
  };

  const saveLunch = async () => {
    if (!lunchForm.week_start) { toast.error('Fecha de inicio de semana requerida'); return; }
    try {
      if (editingLunchId) {
        await api.put(`/api/calendars/lunch/${editingLunchId}`, { week_label: lunchForm.week_label, items: lunchForm.items });
        toast.success('Menú actualizado');
      } else {
        await api.post('/api/calendars/lunch', lunchForm);
        toast.success('Menú creado');
      }
      setShowLunchDialog(false);
      loadLunchMenus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar menú'); }
  };

  const deleteLunch = async (id) => {
    if (!window.confirm('¿Eliminar este menú?')) return;
    try { await api.delete(`/api/calendars/lunch/${id}`); toast.success('Menú eliminado'); loadLunchMenus(); }
    catch (e) { toast.error('Error al eliminar'); }
  };

  // Calendar grid helpers
  const getMonthDays = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Monday = 0
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.event_date === dateStr);
  };

  const changeMonth = (delta) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthName = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const filteredEvents = eventFilterCategory === 'all' ? events : events.filter(e => e.category === eventFilterCategory);

  return (
    <div className="space-y-6" data-testid="calendars-tab">
      {/* Tab switcher */}
      <div className="flex gap-3">
        <Button variant={activeCalendar === 'events' ? 'default' : 'outline'} onClick={() => setActiveCalendar('events')} className={activeCalendar === 'events' ? 'bg-gradient-to-r from-orange-500 to-orange-600' : ''} data-testid="cal-events-tab">
          <Calendar className="w-4 h-4 mr-2" /> Calendario de Eventos
        </Button>
        <Button variant={activeCalendar === 'lunch' ? 'default' : 'outline'} onClick={() => setActiveCalendar('lunch')} className={activeCalendar === 'lunch' ? 'bg-gradient-to-r from-orange-500 to-orange-600' : ''} data-testid="cal-lunch-tab">
          <UtensilsCrossed className="w-4 h-4 mr-2" /> Menú de Almuerzo
        </Button>
      </div>

      {/* ========== EVENTS CALENDAR ========== */}
      {activeCalendar === 'events' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                <h2 className="text-xl font-bold text-slate-800">Calendario de Eventos</h2>
              </div>
              <p className="text-sm text-slate-500 ml-5">Gestionar eventos, reuniones, feriados y actividades</p>
            </div>
            <Button onClick={openCreateEvent} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="new-event-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Evento
            </Button>
          </div>

          {/* Calendar Grid */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}><ChevronLeft className="w-5 h-5" /></Button>
                <h3 className="text-lg font-semibold text-slate-800 capitalize">{monthName()}</h3>
                <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}><ChevronRight className="w-5 h-5" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                  <div key={d} className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500 uppercase">{d}</div>
                ))}
                {getMonthDays().map((day, i) => {
                  const dayEvents = getEventsForDay(day);
                  const isToday = day && `${currentMonth}-${String(day).padStart(2, '0')}` === todayLocalStr();
                  return (
                    <div key={i} className={`bg-white min-h-[80px] p-1 ${!day ? 'bg-slate-50' : ''}`}>
                      {day && (
                        <>
                          <span className={`text-xs font-medium inline-block w-6 h-6 text-center leading-6 rounded-full ${isToday ? 'bg-orange-500 text-white' : 'text-slate-600'}`}>{day}</span>
                          <div className="mt-1 space-y-0.5">
                            {dayEvents.slice(0, 3).map(ev => {
                              const cat = getCategoryInfo(ev.category);
                              return <div key={ev.id} className={`text-[10px] truncate px-1 py-0.5 rounded ${cat.color} cursor-pointer`} title={ev.title} onClick={() => openEditEvent(ev)}>{ev.title}</div>;
                            })}
                            {dayEvents.length > 3 && <div className="text-[10px] text-slate-400">+{dayEvents.length - 3} más</div>}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Events List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Lista de Eventos</CardTitle>
                <select value={eventFilterCategory} onChange={(e) => setEventFilterCategory(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm" data-testid="event-filter-category">
                  <option value="all">Todas las categorías</option>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEvents ? (
                <div className="text-center py-8"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div></div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No hay eventos. Crea el primer evento.</div>
              ) : (
                <div className="space-y-2">
                  {filteredEvents.map(ev => {
                    const cat = getCategoryInfo(ev.category);
                    return (
                      <div key={ev.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-2 h-8 rounded-full ${cat.color.replace('bg-', 'bg-').split(' ')[0]}`}></div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">{ev.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-slate-500">{formatLocalDate(ev.event_date, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              <Badge className={`${cat.color} text-[10px]`}>{cat.label}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEditEvent(ev)} data-testid={`edit-event-${ev.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteEvent(ev.id)} data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== LUNCH MENU ========== */}
      {activeCalendar === 'lunch' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
                <h2 className="text-xl font-bold text-slate-800">Menú de Almuerzo</h2>
              </div>
              <p className="text-sm text-slate-500 ml-5">Gestionar menú semanal de almuerzo</p>
            </div>
            <Button onClick={openCreateLunch} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="new-lunch-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Menú Semanal
            </Button>
          </div>

          {loadingLunch ? (
            <div className="text-center py-8"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div></div>
          ) : lunchMenus.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <UtensilsCrossed className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No hay menús creados</p>
              <p className="text-sm text-slate-400">Crea el menú de la semana para los padres</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {lunchMenus.map(menu => (
                <Card key={menu.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <UtensilsCrossed className="w-4 h-4 text-orange-500" />
                          Semana del {new Date(menu.week_start + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </CardTitle>
                        {menu.week_label && <p className="text-sm text-slate-500 mt-1">{menu.week_label}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditLunch(menu)} data-testid={`edit-lunch-${menu.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLunch(menu.id)} data-testid={`delete-lunch-${menu.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Desktop/tablet grid */}
                    <div className="hidden sm:grid sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {DAYS.map(day => {
                        const item = menu.items?.find(i => i.day === day);
                        return (
                          <div key={day} className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{DAY_LABELS[day]}</p>
                            <p className="text-sm font-medium text-slate-800 break-words">{item?.menu || '-'}</p>
                            {item?.notes && <p className="text-xs text-slate-500 mt-1 break-words">{item.notes}</p>}
                          </div>
                        );
                      })}
                    </div>
                    {/* Mobile stacked list */}
                    <div className="sm:hidden space-y-2" data-testid={`lunch-mobile-list-${menu.id}`}>
                      {DAYS.map(day => {
                        const item = menu.items?.find(i => i.day === day);
                        return (
                          <div key={day} className="bg-slate-50 rounded-lg p-3 flex items-start gap-3">
                            <span className="text-xs font-semibold text-slate-500 uppercase w-16 flex-shrink-0 pt-0.5">{DAY_LABELS[day]}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 break-words">{item?.menu || '-'}</p>
                              {item?.notes && <p className="text-xs text-slate-500 mt-1 break-words">{item.notes}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{editingEventId ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Título *</Label><Input value={eventForm.title} onChange={(e) => setEventForm({...eventForm, title: e.target.value})} data-testid="event-title" /></div>
            <div><Label>Descripción</Label><Textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} rows={2} data-testid="event-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fecha *</Label><Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({...eventForm, event_date: e.target.value})} data-testid="event-date" /></div>
              <div><Label>Fecha fin (opcional)</Label><Input type="date" value={eventForm.end_date} onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})} /></div>
            </div>
            <div>
              <Label>Categoría</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {CATEGORIES.map(cat => (
                  <button key={cat.value} type="button" onClick={() => setEventForm({...eventForm, category: cat.value})}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${eventForm.category === cat.value ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`event-cat-${cat.value}`}>
                    <cat.icon className="w-4 h-4 mx-auto mb-1" />
                    <span className="text-xs font-medium">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancelar</Button>
            <Button onClick={saveEvent} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="save-event-btn">
              {editingEventId ? 'Guardar Cambios' : 'Crear Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lunch Dialog */}
      <Dialog open={showLunchDialog} onOpenChange={setShowLunchDialog}>
        <DialogContent className="max-w-2xl" data-testid="lunch-dialog">
          <DialogHeader>
            <DialogTitle>{editingLunchId ? 'Editar Menú Semanal' : 'Nuevo Menú Semanal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Lunes de la semana *</Label><Input type="date" value={lunchForm.week_start} onChange={(e) => setLunchForm({...lunchForm, week_start: e.target.value})} data-testid="lunch-week-start" /></div>
              <div><Label>Etiqueta (opcional)</Label><Input value={lunchForm.week_label} onChange={(e) => setLunchForm({...lunchForm, week_label: e.target.value})} placeholder="Ej: Semana de bienvenida" /></div>
            </div>
            <div className="space-y-3">
              {DAYS.map((day, idx) => (
                <div key={day} className="grid grid-cols-[100px_1fr_1fr] gap-3 items-center">
                  <Label className="text-sm font-semibold text-slate-600">{DAY_LABELS[day]}</Label>
                  <Input placeholder="Menú del día..." value={lunchForm.items[idx]?.menu || ''} onChange={(e) => updateLunchItem(idx, 'menu', e.target.value)} data-testid={`lunch-menu-${day}`} />
                  <Input placeholder="Notas (opcional)" value={lunchForm.items[idx]?.notes || ''} onChange={(e) => updateLunchItem(idx, 'notes', e.target.value)} />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLunchDialog(false)}>Cancelar</Button>
            <Button onClick={saveLunch} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="save-lunch-btn">
              {editingLunchId ? 'Guardar Cambios' : 'Crear Menú'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarsTab;
